import {
	ActionKey,
	DialogCancellableOpenResult,
	DialogOpenPromise,
	DialogService,
} from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';

import {
	BaseDialog,
	BaseDialogConstructor,
	BaseDialogModel,
} from '~/components/dialog/base-dialog';
// tslint:disable-next-line no-circular-imports
import {
	SimpleDialog,
	SimpleDialogModel,
	SimpleDialogModelDefaults,
} from '~/components/dialog/simple-dialog';
import { Key } from '~/utils/key';

/**
 * See https://aurelia.io/docs/plugins/dialog
 *
 */
@autoinject
export class DialogUtils {
	constructor(private readonly dialogService: DialogService) {}

	/**
	 * Show a simple dialog with an OK or Cancel option by default.
	 *
	 * @param {SimpleDialogModel}	dialogOptions	Dialog options
	 * @param {DialogType}	dialogOptions.simpleDialogType	Determines the type of Dialog to use
	 * @param {DialogWidth}	dialogOptions.dialogWidth	Determines the width of Dialog to use
	 * @param {string}	dialogOptions.bodyText	Text used in the dialog body. If not specified, dialog body will not be rendered
	 * @param {Function}	dialogOptions.okFunction	Method that fires when OK option selected. Optional
	 * @param {boolean}	dialogOptions.okButtonOnly	Only OK button displayed on simple dialog if true. Otherwise both OK and Cancel buttons displayed. Optional
	 * @param {string}	dialogOptions.headingText	Text used in the dialog header. If not specified, defaults to header based on genericDialogType
	 * @param {string}	dialogOptions.okButtonText	Text used in the OK button. If not specified, defaults to 'Ok'
	 * @param {string}	dialogOptions.cancelButtonText	Text used in the Cancel button. If not specified, defaults to 'Cancel'
	 */
	public showDialog = (
		dialogOptions: SimpleDialogModel & {
			okFunction?: () => void;
		} = SimpleDialogModelDefaults,
	) =>
		this.dialogService
			.open({
				viewModel: SimpleDialog,
				model: this.getSimpleDialogModel(dialogOptions),
				lock: true,
				keyboard: [Key.ENTER, Key.ESCAPE],
			})
			.whenClosed((value) => {
				if (!value.wasCancelled && dialogOptions.okFunction) {
					dialogOptions.okFunction();
				}
				return value;
			});

	/**
	 * Show a simple custom dialog that needs to takes a dialog component and model as parameters.
	 * The DialogOpenPromise will allow you to control what happens when the dialog is opened.
	 * Then code execution when dialog is closed is accessed through the closeResult from the DialogOpenResult
	 * Alternatively, if only the close result is required, call whenClosed on the DialogOpenPromise
	 *
	 * Note: Webpack users should always mark dynamically loaded dependencies with PLATFORM.moduleName
	 * For more details do check the `aurelia-webpack-plugin` [wiki](https://github.com/aurelia/webpack-plugin/wiki/Debugging-missing-modules)
	 *
	 * @param {object} dialogObject The dialog component to be used
	 * @param {any} dialogModel The model used in the dialog component
	 * @param {boolean | ActionKey | ActionKey[]} keyboardSetting see dialog-settings.d.ts
	 * @returns {DialogOpenPromise<DialogCancellableOpenResult>}
	 */
	public showCustomDialog = <DialogModel extends BaseDialogModel>(
		dialogObject:
			| BaseDialogConstructor<DialogModel>
			| BaseDialog<DialogModel>,
		dialogModel: DialogModel,
	): DialogOpenPromise<DialogCancellableOpenResult> =>
		this.dialogService.open({
			keyboard: false,
			lock: true,
			model: dialogModel,
			viewModel: dialogObject,
		});

	public getSimpleDialogModel = (
		dialogModel: SimpleDialogModel,
	): SimpleDialogModel => ({
		...SimpleDialogModelDefaults,
		...dialogModel,
		headingText:
			dialogModel.headingText ||
			SimpleDialog.HeadingText[dialogModel.simpleDialogType],
	});

	public closeAll() {
		this.dialogService.closeAll();
	}
}
